import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://shivasecurity.com';
const SITE_NAME = 'Shiva Security Services';
const DEFAULT_IMAGE = `${SITE_URL}/images/home-hero.png`;

const pageMeta = {
    '/': {
        title: 'Shiva Security Services | Security Guards in Bangalore',
        description: 'PSARA licensed security agency in Bangalore providing trained security guards, corporate security, residential security, event security, and security training.',
        keywords: 'security guards Bangalore, security agency Bangalore, PSARA licensed security company, corporate security Bangalore, residential security guards',
    },
    '/about': {
        title: 'About Shiva Security Services | PSARA Licensed Security Agency',
        description: 'Learn about Shiva Security Services, a trusted Bangalore security agency founded by ex-army leadership with PSARA licensing and professional security operations.',
        keywords: 'about Shiva Security Services, PSARA license Bangalore, security company Bangalore',
    },
    '/services': {
        title: 'Security Services in Bangalore | Guards, Corporate, Residential',
        description: 'Hire trained security guards for corporate offices, apartments, industries, events, VIP protection, armed security, and facility security in Bangalore.',
        keywords: 'security services Bangalore, armed guards Bangalore, corporate security, event security, industrial security',
    },
    '/training': {
        title: 'Security Guard Training in Bangalore | Shiva Security Services',
        description: 'Professional security guard training programs covering discipline, emergency response, site protocols, reporting, and customer handling.',
        keywords: 'security guard training Bangalore, security training, guard training program',
    },
    '/gallery': {
        title: 'Security Team Gallery | Shiva Security Services Bangalore',
        description: 'View Shiva Security Services teams, security briefings, on-site deployment, training, and professional guard operations.',
        keywords: 'security guard gallery, Shiva Security Services photos, security team Bangalore',
    },
    '/clients': {
        title: 'Our Clients | Shiva Security Services Bangalore',
        description: 'Shiva Security Services is trusted by businesses, builders, banks, offices, residential communities, and institutions across Bangalore.',
        keywords: 'security clients Bangalore, trusted security agency, Shiva Security clients',
    },
    '/careers': {
        title: 'Security Jobs in Bangalore | Careers at Shiva Security Services',
        description: 'Apply for security guard, housekeeping, supervisor, and operations roles at Shiva Security Services in Bangalore.',
        keywords: 'security guard jobs Bangalore, housekeeping jobs Bangalore, security supervisor jobs',
    },
    '/contact': {
        title: 'Contact Shiva Security Services | Security Agency Bangalore',
        description: 'Contact Shiva Security Services for security guards and facility security in Bangalore. Call +91-9606924394 or request a quote online.',
        keywords: 'contact Shiva Security Services, security agency contact Bangalore, hire security guards Bangalore',
    },
};

const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: SITE_NAME,
    url: SITE_URL,
    image: DEFAULT_IMAGE,
    logo: `${SITE_URL}/images/logo.png`,
    telephone: '+919606924394',
    email: 'enquiry@shivasecurity.com',
    priceRange: '$$',
    address: {
        '@type': 'PostalAddress',
        streetAddress: '#53/3C, Ground Floor, 20th Cross Road, Raghuvendra Layout, Hulimavu, Bannerghatta Road',
        addressLocality: 'Bangalore',
        addressRegion: 'Karnataka',
        postalCode: '560076',
        addressCountry: 'IN',
    },
    geo: {
        '@type': 'GeoCoordinates',
        latitude: 12.879273,
        longitude: 77.603355,
    },
    openingHoursSpecification: [
        {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            opens: '10:00',
            closes: '18:00',
        },
    ],
    areaServed: ['Bangalore', 'Bangalore Rural', 'Karnataka'],
    sameAs: [],
};

function setMeta(selector, attribute, value) {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement('meta');
        const match = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (match) {
            element.setAttribute(match[1], match[2]);
        }
        document.head.appendChild(element);
    }
    element.setAttribute(attribute, value);
}

function setLink(rel, href) {
    let element = document.head.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }
    element.setAttribute('href', href);
}

function setJsonLd(id, data) {
    let element = document.getElementById(id);
    if (!element) {
        element = document.createElement('script');
        element.id = id;
        element.type = 'application/ld+json';
        document.head.appendChild(element);
    }
    element.textContent = JSON.stringify(data);
}

export const SEO = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        const cleanPath = pathname.replace(/\/$/, '') || '/';
        const meta = pageMeta[cleanPath] || {
            title: 'Page Not Found | Shiva Security Services',
            description: 'The requested page could not be found on Shiva Security Services.',
            keywords: 'Shiva Security Services',
            noindex: true,
        };
        const canonical = `${SITE_URL}${cleanPath === '/' ? '/' : cleanPath}`;

        document.title = meta.title;
        setMeta('meta[name="description"]', 'content', meta.description);
        setMeta('meta[name="keywords"]', 'content', meta.keywords);
        setMeta('meta[name="robots"]', 'content', meta.noindex ? 'noindex, follow' : 'index, follow');
        setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);
        setMeta('meta[property="og:title"]', 'content', meta.title);
        setMeta('meta[property="og:description"]', 'content', meta.description);
        setMeta('meta[property="og:type"]', 'content', 'website');
        setMeta('meta[property="og:url"]', 'content', canonical);
        setMeta('meta[property="og:image"]', 'content', DEFAULT_IMAGE);
        setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
        setMeta('meta[name="twitter:title"]', 'content', meta.title);
        setMeta('meta[name="twitter:description"]', 'content', meta.description);
        setMeta('meta[name="twitter:image"]', 'content', DEFAULT_IMAGE);
        setLink('canonical', canonical);
        setJsonLd('local-business-schema', localBusinessSchema);
    }, [pathname]);

    return null;
};
